import { apiClient } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type { CourseRequest, CourseRequestImportResult } from "../types";

export async function listCourseRequests(
  scheduleId: string,
): Promise<CourseRequest[]> {
  const response = await apiClient.get<ApiResponse<CourseRequest[]>>(
    `/schedules/${scheduleId}/course-requests`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load course requests");
}

/**
 * Downloads the schedule's course requests as a CSV (round-trip compatible with
 * the importer) and saves it via a temporary object-URL anchor. Used to copy a
 * curated request set between environments for testing. The response is a raw
 * blob — a browser XHR does NOT honor the server's Content-Disposition filename,
 * so we set the download name ourselves.
 */
export async function exportCourseRequests(scheduleId: string): Promise<void> {
  const response = await apiClient.get(
    `/schedules/${scheduleId}/course-requests/export`,
    { responseType: "blob" },
  );

  const url = URL.createObjectURL(response.data as Blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `course-requests-${scheduleId}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Uploads a CSV of course requests via `multipart/form-data`. Critically we
 * do NOT set `Content-Type` ourselves — the browser must add it WITH the
 * boundary parameter. Passing `Content-Type: null` is axios's documented way
 * to instruct it to skip merging the default JSON header from
 * `apiClient.defaults.headers` for this single request (`undefined` happens
 * to work today but isn't part of axios's documented contract; `null` is).
 *
 * The header value type is `null` here even though axios's `RawAxiosHeaders`
 * lists string-only values, so we cast through the documented escape hatch.
 */
export async function importCourseRequests(
  scheduleId: string,
  file: File,
  replaceAll: boolean,
  signal?: AbortSignal,
): Promise<CourseRequestImportResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("replaceAll", String(replaceAll));

  const response = await apiClient.post<ApiResponse<CourseRequestImportResult>>(
    `/schedules/${scheduleId}/course-requests/import`,
    form,
    {
      headers: {
        // axios reads `null` as "remove the default for this request" — the
        // browser then attaches `multipart/form-data; boundary=…` itself.
        "Content-Type": null,
      } as unknown as Record<string, string>,
      // Threaded from the upload panel so unmount / new-file selection can
      // abort the in-flight request rather than waiting on the server. axios
      // converts an AbortSignal abort into a `CanceledError` we swallow in the
      // panel's catch block.
      signal,
    },
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to import course requests");
}
